import { Fragment, ReactNode, forwardRef } from 'react';
import { Outlet } from 'react-router-dom';

import { HeaderTabs } from '@backstage/core-components';
import { attachComponentData } from '@backstage/core-plugin-api';

import {
  PageLayout,
  PageLayoutProps,
  PropsBridgeProvider,
  usePropsBridge,
} from '@spotify/confidence-core-react';

import { PageBreadcrumbs } from '../PageBreadcrumbs';
import { PageRootBridgeProps } from '../types';
import { PageRouteRef } from '../utils';
import { PAGE_CONTENT_KEY, usePageContentChildren } from './composition';
import { PageContentBridgeProps } from './types';
import { useActionBar } from './useActionBar';
import { useContentOffsets } from './useContentOffsets';
import { useTabs } from './useTabs';

type PageLayoutPassthrough = Omit<
  PageLayoutProps,
  | 'title'
  | 'pageTitleOverride'
  | 'headerBreadcrumbs'
  | 'children'
  | 'sideBarLeft'
  | 'sideBarRight'
>;

export type PageContentProps = PageLayoutPassthrough & {
  children: ReactNode;
};

export const ContentTabsHeader = ({
  routeRefs,
}: {
  routeRefs: PageRouteRef[];
}) => {
  const { tabs, selectedIndex } = useTabs(routeRefs);
  return <HeaderTabs selectedIndex={selectedIndex} tabs={tabs} />;
};

export const Content = forwardRef<HTMLDivElement, PageContentProps>(
  (
    {
      children,
      headerTabs: headerTabsProp,
      headerTabsRef: headerTabsRefProp,
      ...layoutProps
    },
    ref,
  ) => {
    const bridgeProps = usePropsBridge<
      PageRootBridgeProps & { contentTopOffset?: number }
    >();

    const { descriptor, leafLabel, wrapperElements } = bridgeProps;

    const {
      sideBarLeft,
      sideBarRight,
      actionBar,
      tabsRouteRefs,
      mainChildren,
    } = usePageContentChildren(children);

    const {
      actionBarRef,
      tabsRef,
      isNested,
      actionBarTopOffset,
      contentTopOffset,
    } = useContentOffsets(bridgeProps.contentTopOffset);

    const renderedActionBar = useActionBar({
      wrapperElements,
      actionBar,
      actionBarRef,
      actionBarTopOffset,
    });

    const body = (
      <PropsBridgeProvider<PageContentBridgeProps>
        {...bridgeProps}
        contentTopOffset={contentTopOffset}
      >
        {mainChildren}
        {tabsRouteRefs && <Outlet />}
      </PropsBridgeProvider>
    );

    if (isNested) {
      return (
        <Fragment>
          {renderedActionBar}
          {body}
        </Fragment>
      );
    }

    const pageTitleOverride =
      typeof leafLabel === 'string' ? leafLabel : descriptor.title;

    const headerTabs = tabsRouteRefs ? (
      <ContentTabsHeader routeRefs={tabsRouteRefs} />
    ) : (
      headerTabsProp
    );
    const headerTabsRef = tabsRouteRefs ? tabsRef : headerTabsRefProp;

    return (
      <PageLayout
        ref={ref}
        {...layoutProps}
        sideBarLeft={sideBarLeft}
        sideBarRight={sideBarRight}
        title={leafLabel}
        pageTitleOverride={pageTitleOverride}
        headerBreadcrumbs={<PageBreadcrumbs />}
        headerTabs={headerTabs}
        headerTabsRef={headerTabsRef}
      >
        {renderedActionBar}
        {body}
      </PageLayout>
    );
  },
);
Content.displayName = 'Content';

attachComponentData(Content, PAGE_CONTENT_KEY, true);
